/**
 * Exam timer service for managing exam-level time tracking
 * This is separate from question-level timing and handles the entire exam session
 */
class ExamTimerService {
  constructor() {
    this.startTime = null;
    this.totalTimeSpent = 0;
    this.isRunning = false;
    this.updateCallback = null;
    this.updateInterval = null;
    this.lastUpdateTime = 0;
  }

  /**
   * Start the exam timer
   * @param {number} initialTimeSpent - Previous time spent in seconds (for resume functionality)
   * @param {function} onTimeUpdate - Callback function to send updates to backend
   */
  start(initialTimeSpent = 0, onTimeUpdate = null) {
    if (this.isRunning) {
      // console.log('[ExamTimerService] Timer already running');
      return;
    }

    this.totalTimeSpent = initialTimeSpent;
    this.startTime = Date.now() - (initialTimeSpent * 1000);
    this.isRunning = true;
    this.updateCallback = onTimeUpdate;
    this.lastUpdateTime = Date.now();

    // console.log(`[ExamTimerService] Started timer with initial time: ${initialTimeSpent}s`);

    // Set up interval to send updates every 60 seconds
    this.updateInterval = setInterval(() => {
      this.sendUpdate();
    }, 60000); // 60 seconds

    // Send initial update immediately
    if (this.updateCallback) {
      this.updateCallback(this.totalTimeSpent);
    }
  }

  /**
   * Stop the exam timer
   * @returns {number} Final time spent in seconds
   */
  stop() {
    if (!this.isRunning) {
      // console.log('[ExamTimerService] Timer not running');
      return this.totalTimeSpent;
    }

    const finalTime = this.getCurrentTimeSpent();
    this.isRunning = false;
    
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    // Send final update
    if (this.updateCallback) {
      this.updateCallback(finalTime);
    }

    // console.log(`[ExamTimerService] Stopped timer. Final time: ${finalTime}s`);
    return finalTime;
  }

  /**
   * Pause the timer (for navigation away from exam, etc.)
   */
  pause() {
    if (!this.isRunning) return;

    this.totalTimeSpent = this.getCurrentTimeSpent();
    this.isRunning = false;
    
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    // console.log(`[ExamTimerService] Paused timer at: ${this.totalTimeSpent}s`);
  }

  /**
   * Resume the timer from current totalTimeSpent
   */
  resume() {
    if (this.isRunning) return;

    this.startTime = Date.now() - (this.totalTimeSpent * 1000);
    this.isRunning = true;
    this.lastUpdateTime = Date.now();

    // Resume update interval
    this.updateInterval = setInterval(() => {
      this.sendUpdate();
    }, 60000);

    // console.log(`[ExamTimerService] Resumed timer from: ${this.totalTimeSpent}s`);
  }

  /**
   * Get current time spent in seconds
   * @returns {number} Current time spent
   */
  getCurrentTimeSpent() {
    if (!this.isRunning || !this.startTime) {
      return this.totalTimeSpent;
    }
    
    return Math.floor((Date.now() - this.startTime) / 1000);
  }  /**
   * Send time update to backend via callback
   */
  sendUpdate() {
    if (!this.isRunning || !this.updateCallback) return;

    const currentTime = this.getCurrentTimeSpent();
    
    // Only send update if at least 60 seconds have passed since last update
    const timeSinceLastUpdate = Date.now() - this.lastUpdateTime;
    if (timeSinceLastUpdate >= 60000) {
      // console.log(`[ExamTimerService] Sending time update: ${currentTime}s`);
      try {
        this.updateCallback(currentTime);
        this.lastUpdateTime = Date.now();
      } catch (error) {
        console.error('[ExamTimerService] Error in update callback:', error);
        // Don't stop the timer due to callback errors, let the parent component handle it
      }
    }
  }

  /**
   * Force send an immediate update (for exam end, etc.)
   */
  forceUpdate() {
    if (!this.updateCallback) return;

    const currentTime = this.getCurrentTimeSpent();
    // console.log(`[ExamTimerService] Force sending time update: ${currentTime}s`);
    this.updateCallback(currentTime);
    this.lastUpdateTime = Date.now();
  }

  /**
   * Check if timer should be stopped due to exam status
   * @param {string} examStatus - Current exam status
   */
  checkExamStatus(examStatus) {
    if (examStatus === 'Finished' && this.isRunning) {
      // console.log('[ExamTimerService] Exam is finished, stopping timer automatically');
      this.stop();
      return false; // Timer stopped
    }
    return this.isRunning; // Timer still running or was already stopped
  }

  /**
   * Check if timer is currently running
   * @returns {boolean} True if running
   */
  getIsRunning() {
    return this.isRunning;
  }
  /**
   * Reset timer completely
   */
  reset() {
    this.stop();
    this.startTime = null;
    this.totalTimeSpent = 0;
    this.updateCallback = null;
    // console.log('[ExamTimerService] Timer reset');
  }

  /**
   * Set the total time spent for finished exams (for review mode)
   * @param {number} timeSpent - Total time spent in seconds
   */
  setTimeSpentForReview(timeSpent) {
    this.stop(); // Ensure timer is stopped
    this.totalTimeSpent = timeSpent;
    this.startTime = null;
    // console.log(`[ExamTimerService] Set time for review mode: ${timeSpent}s`);
  }
}

// Export singleton instance
const examTimerService = new ExamTimerService();
export default examTimerService;
